import React, { useMemo, useState } from 'react'
import { Box, Stack, Typography, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import TemporaryIcon from 'components/global/TemporaryIcon'
import EmptyScreen from 'packages/ui/EmptyScreen'
import ScrollTable from 'components/global/CUK/ScrollTable'
import TableSectionSkeleton from '../../../Skeletons/TableSectionSkeleton'

import { formatBytesWithUnit } from '../../../helpers'
import useGraph from './use-graph'

const EvolutionSection = () => {
  const { t, i18n } = useTranslation()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const {loading, data} = useGraph()

  const formattedData = useMemo(() => {
    return data.map((item) => {
      const monthDate = moment(item.date)
      const pastMonthDate = moment(item.date).subtract(12, 'months')

      return {
        month: monthDate.format('MMMM'),
        currentYear: monthDate.year(),
        pastYear: pastMonthDate.year(),
        consumptionLastYear: item.consumption_past_year,
        consumptionCurrentYear: item.consumption_current_year,
        consumptionGrowth: item.percentage_consumption.toFixed(0),
        activeUsersLastYear: item.active_users_past_year || 0,
        activeUsersCurrentYear: item.active_users_current_year || 0,
        activeUsersGrowth: item.percentage_active_users.toFixed(0),
        hasCurrentData: item.consumption_current_year > 0,
        hasPastData: item.consumption_past_year > 0
      }
    }).reverse()
  }, [data])

  if (loading) {
    return <TableSectionSkeleton />
  }

  if (!formattedData.length) {
    return (
      <Box sx={{ 
        width: '100%',
        border: '1px dashed #D7DAEA',
        borderRadius: '16px',
        padding: 3,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <EmptyScreen 
          icon="square-poll-vertical" 
          variant="compact"
          iconSize="32px" 
          fontSize="12px"
          spacing={2}
        >
          {t('stats.evolution.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  return (
    <Box sx={{ 
      width: { xs: '100%', md: '40%' },
      border: '1px solid #EFEFEF',
      borderRadius: '16px', 
      p: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography sx={{ 
        fontWeight: 600, 
        fontSize: '12px', 
        mb: 2, 
        textTransform: 'uppercase',
        textAlign: 'center'
      }}>
        {t('stats.evolution.table_title')}
      </Typography>
      
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <ScrollTable height={140} shouldFetchNextPage={false}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%', minWidth: '120px' }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                    {t('stats.evolution.month')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '20%', minWidth: '100px', textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                    {t('stats.evolution.past_year')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '20%', minWidth: '100px', textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                    {t('stats.evolution.current_year')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '20%', minWidth: '80px', textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                    {t('stats.evolution.trend')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formattedData.map((row, index) => {
                const isHovered = hoveredIndex === index
                const { value: lastYearValue, unit: lastYearUnit } = formatBytesWithUnit(row.consumptionLastYear, i18n.language)
                const { value: currentYearValue, unit: currentYearUnit } = formatBytesWithUnit(row.consumptionCurrentYear, i18n.language)
                
                return (
                  <TableRow 
                    key={`${row.month}-${index}`}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: isHovered ? '#F5F5F5' : 'transparent',
                      '&:hover': {
                        backgroundColor: '#F5F5F5'
                      }
                    }}
                  >
                    <TableCell sx={{ width: '40%' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500, textTransform: 'capitalize' }}>
                        {row.month}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '20%', textAlign: 'center' }}>
                      <Stack alignItems="center">
                        {row.hasPastData ? (
                          <>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                              {lastYearValue}
                              <Typography component="span" sx={{ fontSize: '10px', color: '#989898', ml: 0.5 }}>
                                {lastYearUnit}
                              </Typography>
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: '#989898' }}>
                              {row.pastYear}
                            </Typography>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: '12px', color: '#989898' }}>-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ width: '20%', textAlign: 'center' }}>
                      <Stack alignItems="center">
                        {row.hasCurrentData ? (
                          <>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                              {currentYearValue}
                              <Typography component="span" sx={{ fontSize: '10px', color: '#989898', ml: 0.5 }}>
                                {currentYearUnit}
                              </Typography>
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: '#989898' }}>
                              {row.currentYear}
                            </Typography>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: '12px', color: '#989898' }}>-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ width: '20%', textAlign: 'center' }}>
                      {row.hasCurrentData && row.hasPastData ? (
                        <Typography sx={{ 
                          fontSize: '12px', 
                          color: row.consumptionGrowth >= 0 ? '#587F31' : '#DC2626',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5
                        }}>
                          {row.consumptionGrowth >= 0 ? '+' : ''}{row.consumptionGrowth}%
                          <TemporaryIcon 
                            iconlabel={row.consumptionGrowth >= 0 ? 'arrow-trend-up' : 'arrow-trend-down'}
                            iconsize="8px" 
                          />
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: '12px', color: '#989898' }}>-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </ScrollTable>
      </Box>
    </Box>
  )
}

export default EvolutionSection